<template>
    <UserTabs
        :selected-user="selectedUser"
        :personal-team="userPersonalTeam"
        tab-key="user-blocked-actions"
    >
        <div>
            <h3
                class="mt-12 text-xl font-medium leading-normal tracking-tighter text-gray-600"
            >
                {{ t("vue.add_blocked_actions_for_user") }}
            </h3>
            <div class="mt-2 flex gap-2">
                <div
                    v-for="item in blockable"
                    :key="item"
                    class="inline-flex cursor-pointer rounded-md bg-red-200 px-2 py-[3px] hover:bg-red-300"
                    @click="handleAddBlockedAction(item)"
                >
                    {{ item }}
                </div>
            </div>
        </div>

        <DataTable
            class="mt-8"
            :title="`${t('vue.blocked_actions_of')} ${selectedUser.name}`"
            :headers="headers"
            :items="blockedActions"
        >
            <template #item-admin="{ data }">
                <div class="flex p-6 text-gray-500">
                    <div>
                        {{ data.email }}
                    </div>
                </div>
            </template>

            <template #item-created_at="{ data }">
                <div class="flex justify-center p-6 text-gray-500">
                    <div v-if="data">
                        {{ format(new Date(data), "d. L. yyyy") }}
                    </div>
                </div>
            </template>

            <template #item-custom="{ row }">
                <AtomicButton
                    color="red"
                    size="sm"
                    @click="handleDelete(row.id)"
                >
                    <template #icon>
                        <TrashIcon class="h-6 w-6" />
                    </template>
                </AtomicButton>
            </template>
        </DataTable>
    </UserTabs>
</template>
<script lang="ts" setup>
import type IPaginationData from "@/components/Atomic/DataTable/types/IPaginationData";
import type IUser from "@/types/Members/IUser";

import type IDataTableHeader from "@/components/Atomic/DataTable/types/IDataTableHeader";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { create } from "@/composables/useNotifications";
import { TrashIcon } from "@heroicons/vue/24/outline";
import { format } from "date-fns";
import useConfirm from "@/composables/useConfirm";
import type ICurrentTeam from "@/types/Members/ICurrentTeam";
import { useTranslations } from "@/composables/useTranslations";

const props = defineProps<{
    selectedUser: IUser;
    blockedActions: IPaginationData<any>;
    blockable: string[];
    userPersonalTeam: ICurrentTeam;
}>();

// Get access to translations
const { t } = useTranslations();

const { show } = useConfirm();

const headers: IDataTableHeader[] = [
    {
        text: t("vue.action_name"),
        id: "action_name",
    },
    {
        text: t("vue.created_by"),
        id: "admin",
    },
    {
        text: t("vue.date"),
        id: "created_at",
        align: "center",
    },
    {
        text: "",
        id: "custom",
    },
];

const handleAddBlockedAction = async (item: string) => {
    const confirmed = await show({
        header: t("vue.add_blocked_action_header"),
        message: `${t("vue.add_blocked_action_message")} "${item}"?`,
        type: "danger",
        confirm: t("vue.add"),
    });
    if (confirmed) {
        router.post(
            route("kiosk.users.blocked-actions.store", {
                user: props.selectedUser.id,
            }),
            { blockedAction: item },
            {
                preserveScroll: true,
                onSuccess: () => {
                    create({
                        title: t("vue.blocked_action_added"),
                        type: "success",
                    });
                },
                onError: () => {
                    create({
                        title: t("vue.blocked_action_not_added"),
                        text: t("vue.error_adding_blocked_action"),
                        type: "error",
                    });
                },
            },
        );
    }
};

const handleDelete = async (id: number) => {
    const confirmed = await show({
        header: t("vue.delete_blocked_action_header"),
        message: t("vue.delete_blocked_action_message"),
        type: "danger",
        confirm: t("vue.delete"),
    });
    if (confirmed) {
        router.delete(
            route("kiosk.users.blocked-actions.destroy", { blockedAction: id }),
            {
                preserveScroll: true,
                onSuccess: () => {
                    create({
                        title: t("vue.blocked_action_deleted"),
                        type: "success",
                    });
                },
                onError: () => {
                    create({
                        title: t("vue.blocked_action_not_deleted"),
                        text: t("vue.error_deleting_blocked_action"),
                        type: "error",
                    });
                },
            },
        );
    }
};
</script>
